import { useEffect, useRef } from "react";

import { BARS, SWEEP_DURATION_MS, barPathAt } from "./logoMarkGeometry";
import { MARK_VIEW_BOX } from "./logoMarkPaths";

type LogoMarkProps = {
  /** True while the lockup is hovered or focused; drives the cascade. */
  readonly active: boolean;
};

/**
 * The four-bar mark. Bars morph between their even and staggered states in a
 * bottom-up cascade, the bottom bar leading.
 *
 * Motion is progressive enhancement: the resting geometry is rendered on the
 * server-equivalent first paint, `prefers-reduced-motion` snaps straight to the
 * end state, and the rAF loop only runs while a transition is in flight — so it
 * is idle when untouched and browsers park it while the tab is hidden.
 */
export function LogoMark({ active }: LogoMarkProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const sweepRef = useRef(0);
  const frameRef = useRef(0);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const bars = Array.from(svg.querySelectorAll("path"));
    const target = active ? 1 : 0;

    const paint = (sweep: number): void => {
      BARS.forEach((bar, index) => {
        const element = bars[index];
        if (element === undefined) return;
        element.setAttribute("d", barPathAt(bar, sweep));
      });
    };

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      sweepRef.current = target;
      paint(target);
      return;
    }

    let previous: number | null = null;

    const step = (timestamp: number): void => {
      const elapsed = previous === null ? 0 : timestamp - previous;
      previous = timestamp;

      const direction = target > sweepRef.current ? 1 : -1;
      const next = sweepRef.current + direction * (elapsed / SWEEP_DURATION_MS);
      sweepRef.current = direction > 0 ? Math.min(next, target) : Math.max(next, target);

      paint(sweepRef.current);

      if (sweepRef.current !== target) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        frameRef.current = 0;
      }
    };

    frameRef.current = requestAnimationFrame(step);

    return () => {
      if (frameRef.current !== 0) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = 0;
      }
    };
  }, [active]);

  return (
    <svg
      aria-hidden="true"
      className="logo__mark"
      ref={svgRef}
      viewBox={MARK_VIEW_BOX}
      xmlns="http://www.w3.org/2000/svg"
    >
      {BARS.map((bar) => (
        <path className={bar.className} d={barPathAt(bar, 0)} key={bar.className} />
      ))}
    </svg>
  );
}
